/**
 * 体重趋势与热量校准（§20）。
 *
 * 原则：
 *   - 数据不足时不预测（不画误导性趋势线、不生成虚假精确结论）；
 *   - 使用 7 日移动平均减少单日波动；
 *   - 至少积累 14–21 天数据后才自动建议调整热量；
 *   - 每次调整只建议 100–150 kcal；不每天修改目标；
 *   - 输出区间而非单点：如「未来 30 天预计变化 -0.5 至 -1.2 kg」；
 *   - 不输出「今天多吃 X 必然增长 Y 脂肪」的结论。
 *
 * 纯函数：日期与数据全部由调用方传入（规则层不读时钟）。
 */

import type { BodyMeasurement } from "@jilian/shared";
import { RULES_VERSION } from "@jilian/shared";
import type {
  NutritionTargetAdjustmentInput,
  NutritionTargetAdjustmentResult,
  WeightTrendForecast,
  WeightTrendInput,
} from "./interface";

const DAY_MS = 24 * 60 * 60 * 1000;

/** 按日期去重（同一天多条记录取最后一条；推荐相似条件下称重）。 */
export function dedupeByDate(measurements: BodyMeasurement[]): BodyMeasurement[] {
  const byDate = new Map<string, BodyMeasurement>();
  for (const m of measurements) byDate.set(m.date, m); // 后写覆盖先写（调用方应先按时间排序）
  return [...byDate.values()].sort((a, b) => (a.date < b.date ? -1 : 1));
}

/** 均线：最近 N 个自然日窗口内记录的平均值（窗口内 ≥1 条即可；无记录日不取 0）。 */
export function movingAverage(
  sorted: BodyMeasurement[],
  windowDays: number,
): { averageKg: number; usedDays: number; fromDate: string } | null {
  if (sorted.length === 0) return null;
  const latest = sorted[sorted.length - 1]!;
  const latestMs = new Date(latest.date).getTime();
  const floor = latestMs - (windowDays - 1) * DAY_MS;
  const inWindow = sorted.filter((m) => new Date(m.date).getTime() >= floor);
  if (inWindow.length === 0) return null;
  const averageKg = inWindow.reduce((a, m) => a + m.weightKg, 0) / inWindow.length;
  return { averageKg, usedDays: inWindow.length, fromDate: inWindow[0]!.date };
}

/** 最小二乘线性回归 → 斜率 kg/天（数据不足返回 null）。 */
export function linearSlopeKgPerDay(
  sorted: BodyMeasurement[],
): { slope: number; intercept: number; days: number; n: number } | null {
  if (sorted.length < 2) return null;
  const t0 = new Date(sorted[0]!.date).getTime();
  const xs = sorted.map((m) => (new Date(m.date).getTime() - t0) / DAY_MS);
  const ys = sorted.map((m) => m.weightKg);
  const n = xs.length;
  const meanX = xs.reduce((a, b) => a + b, 0) / n;
  const meanY = ys.reduce((a, b) => a + b, 0) / n;
  let num = 0;
  let den = 0;
  for (let i = 0; i < n; i++) {
    num += (xs[i]! - meanX) * (ys[i]! - meanY);
    den += (xs[i]! - meanX) ** 2;
  }
  if (den === 0) return null;
  const slope = num / den;
  const intercept = meanY - slope * meanX;
  return { slope, intercept, days: xs[n - 1]! - xs[0]!, n };
}

/** 体重趋势（§20.3）：7 日均重 + 30 日预计变化区间。 */
export function estimateWeightTrend(input: WeightTrendInput): WeightTrendForecast {
  const { averageDays, projectionDays, minimumDataPoints } = input;
  const sorted = dedupeByDate(input.measurements);

  const avg = movingAverage(sorted, averageDays);
  const hasEnough = sorted.length >= minimumDataPoints;
  const forecastDays = Math.min(projectionDays, 21); // §20.3：基于最近 21 天可靠数据

  if (!hasEnough || !avg) {
    return {
      available: false,
      sevenDayAverageKg: null,
      projectedChangeKg: null,
      reasonText: "当前数据不足，需要更多体重记录后才能估算。",
      ruleVersion: RULES_VERSION,
    };
  }

  // 预计变化：最近 21 天斜率 × 30 天，±0.4 kg 保守带（不显示虚假精确值）
  const window = sorted.filter((m) => {
    const latest = new Date(sorted[sorted.length - 1]!.date).getTime();
    return new Date(m.date).getTime() >= latest - forecastDays * DAY_MS;
  });
  const reg = linearSlopeKgPerDay(window.length >= 2 ? window : sorted);
  if (!reg) {
    return {
      available: false,
      sevenDayAverageKg: Math.round(avg.averageKg * 10) / 10,
      projectedChangeKg: null,
      reasonText: "当前数据不足，需要更多体重记录后才能估算。",
      ruleVersion: RULES_VERSION,
    };
  }

  const change = reg.slope * projectionDays;
  const low = Math.round((change - 0.4) * 10) / 10;
  const high = Math.round((change + 0.4) * 10) / 10;

  return {
    available: true,
    sevenDayAverageKg: Math.round(avg.averageKg * 10) / 10,
    projectedChangeKg: { low, high },
    reasonText: `按最近趋势，未来 ${projectionDays} 天体重预计变化为 ${low > 0 ? "+" : ""}${low.toFixed(1)} 至 ${high > 0 ? "+" : ""}${high.toFixed(1)} kg（基于最近 ${forecastDays} 天数据，区间口径）。`,
    ruleVersion: RULES_VERSION,
  };
}

// ---------------------------------------------------------------------------
// 热量校准（§20.2）
// ---------------------------------------------------------------------------

const WEEK_MS = 7 * DAY_MS;

/**
 * 热量目标调整建议。
 *
 * 必须积累 ≥ minimumWindowDays（14–21 天）数据；每次仅 100–150 kcal；
 * 附带依据；由调用方（UI）在用户确认后生效，且 14 天内不重复调整。
 */
export function evaluateNutritionTargetAdjustment(
  input: NutritionTargetAdjustmentInput,
): NutritionTargetAdjustmentResult {
  const { measurements, currentTarget, minimumWindowDays, minCaloriesStep, maxCaloriesStep } = input;
  const reasonCodes: string[] = [];
  const ruleVersion = RULES_VERSION;

  const insufficient = (text: string): NutritionTargetAdjustmentResult => ({
    action: "insufficient_data",
    suggestedCalories: null,
    reasonCodes: [],
    reasonText: text,
    windowDays: 0,
    ruleVersion,
  });

  const sorted = dedupeByDate(measurements);
  if (sorted.length < 2) return insufficient("当前数据不足，需要更多体重记录后才能估算。");

  const latest = sorted[sorted.length - 1]!;
  const first = sorted[0]!;
  const spanDays = Math.round((new Date(latest.date).getTime() - new Date(first.date).getTime()) / DAY_MS);
  if (spanDays < minimumWindowDays - 1) {
    return insufficient(`当前数据不足（已积累约 ${spanDays} 天，至少需要 ${minimumWindowDays} 天），暂不调整热量目标。`);
  }

  // 冷却期：14 天内不重复调整（防「每日频繁改变目标」）
  if (input.daysSinceLastAdjustment != null && input.daysSinceLastAdjustment < 14) {
    return {
      action: "keep",
      suggestedCalories: currentTarget.calories,
      reasonCodes: ["ADJUSTMENT_COOLDOWN"],
      reasonText: "距上次调整不足 14 天，暂不再次调整热量目标。",
      windowDays: spanDays,
      ruleVersion,
    };
  }

  const reg = linearSlopeKgPerDay(sorted);
  if (!reg) return insufficient("当前数据不足，需要更多体重记录后才能估算。");
  const weeklyKg = reg.slope * 7;

  // 调整方向信号
  const rising = weeklyKg > 0.25; // 长期趋势不符合减脂目标（约 +0.25 kg/周）
  const fallingTooFast = weeklyKg < -0.5; // 体重下降过快
  const performanceDecline = input.trainingTrendDeclining === true;
  const recoveryWorse = input.recoveryWorsening === true;

  // 调整档位：100–150 kcal，默认取最小档（保守，避免激进调目标）
  const step = Math.min(maxCaloriesStep, Math.max(minCaloriesStep, 100));

  if (rising && !fallingTooFast) {
    reasonCodes.push("WEIGHT_TREND_RISING");
    return {
      action: "decrease_100_150",
      suggestedCalories: Math.max(1200, currentTarget.calories - step),
      reasonCodes,
      reasonText: `21 天窗口内体重趋势约 ${weeklyKg > 0 ? "+" : ""}${weeklyKg.toFixed(2)} kg/周，不符合当前减脂目标；建议热量 -${step} kcal（当前 ${currentTarget.calories} → ${Math.max(1200, currentTarget.calories - step)} kcal）。`,
      windowDays: spanDays,
      ruleVersion,
    };
  }

  if (fallingTooFast || performanceDecline || recoveryWorse) {
    if (fallingTooFast) reasonCodes.push("WEIGHT_TREND_FALLING_TOO_FAST");
    if (performanceDecline) reasonCodes.push("TRAINING_PERFORMANCE_DECLINE");
    if (recoveryWorse) reasonCodes.push("RECOVERY_WORSENING");
    return {
      action: "increase_100_150",
      suggestedCalories: currentTarget.calories + step,
      reasonCodes,
      reasonText: `依据：${
        fallingTooFast ? `体重下降过快（${weeklyKg.toFixed(2)} kg/周）；` : ""
      }${performanceDecline ? "多个动作表现持续下降；" : ""}${recoveryWorse ? "恢复明显变差；" : ""}建议热量 +${step} kcal（当前 ${currentTarget.calories} → ${currentTarget.calories + step} kcal）。`,
      windowDays: spanDays,
      ruleVersion,
    };
  }

  // 保持：体重缓慢下降或基本稳定，训练表现维持
  return {
    action: "keep",
    suggestedCalories: currentTarget.calories,
    reasonCodes: ["WEIGHT_TREND_STABLE"],
    reasonText: `体重趋势约 ${weeklyKg > 0 ? "+" : ""}${weeklyKg.toFixed(2)} kg/周，处于保持区间，暂不调整热量目标。`,
    windowDays: spanDays,
    ruleVersion,
  };
}

export { WEEK_MS };